import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AdminBalance, MovementType } from '../types/inventory.js';

export interface StockAdjustmentDialogProps {
  balance: AdminBalance | null;
  onClose: () => void;
  onSubmit: (input: { movementType: MovementType; quantity: number; note: string | null }) => void;
  isSubmitting: boolean;
}

/** Only the two movement types an admin manually applies in this UI -- `reservation`/`release` are order-lifecycle-driven (see docs/inventory-architecture.md), not something an admin picks from a dropdown here. */
const MANUAL_TYPES: { value: MovementType; label: string }[] = [
  { value: 'inbound', label: 'Stock in (inbound)' },
  { value: 'outbound', label: 'Stock out (outbound)' },
  { value: 'adjustment', label: 'Manual correction (+/-)' },
];

/** Same accessible-dialog pattern as AdminConfirmDialog (focus-on-open, Escape-to-cancel, backdrop-click-to-cancel) plus the form fields a plain confirm dialog doesn't have. */
export function StockAdjustmentDialog({ balance, onClose, onSubmit, isSubmitting }: StockAdjustmentDialogProps) {
  const [movementType, setMovementType] = useState<MovementType>('inbound');
  const [quantity, setQuantity] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const quantityId = useId();
  const noteId = useId();
  const quantityRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!balance) return;
    quantityRef.current?.focus();
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [balance, onClose]);

  if (!balance) return null;

  function handleConfirm() {
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || qty === 0) {
      setError('Enter a non-zero quantity.');
      return;
    }
    if (movementType !== 'adjustment' && qty < 0) {
      setError('Only a manual correction may use a negative quantity.');
      return;
    }
    setError(null);
    onSubmit({ movementType, quantity: qty, note: note.trim() || null });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
      >
        <h2 className="text-base font-semibold text-gray-900">
          Adjust stock — {balance.productName} ({balance.sku})
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Warehouse {balance.warehouseCode}. Currently {balance.quantityAvailable ?? 0} available. Every
          adjustment creates a permanent, auditable movement record.
        </p>

        <div className="mt-4 space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Movement type</label>
            <select
              value={movementType}
              onChange={(event) => setMovementType(event.target.value as MovementType)}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            >
              {MANUAL_TYPES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={quantityId} className="mb-1 block text-xs font-medium text-gray-600">
              Quantity {movementType === 'adjustment' && '(negative allowed for a downward correction)'}
            </label>
            <input
              ref={quantityRef}
              id={quantityId}
              inputMode="numeric"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              aria-invalid={Boolean(error)}
              aria-describedby={error ? `${quantityId}-error` : undefined}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          <div>
            <label htmlFor={noteId} className="mb-1 block text-xs font-medium text-gray-600">
              Note (optional)
            </label>
            <textarea
              id={noteId}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2"
            />
          </div>
          {error && (
            <p id={`${quantityId}-error`} className="text-xs text-red-600">
              {error}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="rounded-full border border-gray-200 px-4 py-1.5 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            className="bg-brand text-brand-foreground rounded-full px-4 py-1.5 text-sm font-semibold disabled:opacity-60"
          >
            {isSubmitting ? 'Applying…' : 'Apply adjustment'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
